import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CheckInDto, CheckInStatus, TaskDto } from "@habitos/shared";
import { api } from "./api";

export function useHabits(includeArchived = false) {
  return useQuery({ queryKey: ["habits", includeArchived], queryFn: () => api.habits(includeArchived) });
}

export function useGoals() {
  return useQuery({ queryKey: ["goals"], queryFn: api.goals });
}

export function useAiStatus() {
  return useQuery({ queryKey: ["ai-status"], queryFn: api.aiStatus, staleTime: Infinity });
}

export function useCheckIns(from: string, to: string) {
  return useQuery({ queryKey: ["checkins", from, to], queryFn: () => api.checkIns(from, to) });
}

/** Optimistic: a check-in tap has to feel instant, even on a slow connection. */
export function useSetCheckIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { habitId: string; date: string; status: CheckInStatus | null }) =>
      api.setCheckIn(v.habitId, v.date, v.status),
    onMutate: async ({ habitId, date, status }) => {
      await client.cancelQueries({ queryKey: ["checkins"] });
      const snapshots = client.getQueriesData<CheckInDto[]>({ queryKey: ["checkins"] });
      for (const [key, rows] of snapshots) {
        const [, from, to] = key as [string, string, string];
        if (!rows || date < from || date > to) continue;
        const rest = rows.filter((c) => !(c.habitId === habitId && c.date === date));
        client.setQueryData(key, status ? [...rest, { habitId, date, status }] : rest);
      }
      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      for (const [key, rows] of context?.snapshots ?? []) client.setQueryData(key, rows);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["goals"] });
      void client.invalidateQueries({ queryKey: ["stats"] });
      return client.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useTasks(from: string, to: string) {
  return useQuery({ queryKey: ["tasks", from, to], queryFn: () => api.tasks(from, to) });
}

/** Optimistic, like check-ins. */
export function useSetTaskDone() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; done: boolean }) => api.setTaskDone(v.id, v.done),
    onMutate: async ({ id, done }) => {
      await client.cancelQueries({ queryKey: ["tasks"] });
      const snapshots = client.getQueriesData<TaskDto[]>({ queryKey: ["tasks"] });
      for (const [key, rows] of snapshots) {
        if (rows) client.setQueryData(key, rows.map((t) => (t.id === id ? { ...t, done } : t)));
      }
      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      for (const [key, rows] of context?.snapshots ?? []) client.setQueryData(key, rows);
    },
    onSettled: () => client.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

/** "Pasar a mañana": moves unfinished tasks to another day. */
export function useMoveTasks() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.moveTasks,
    onSettled: () => client.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

/** Streaks per habit; a check-in refreshes them. */
export function useHabitStats() {
  return useQuery({ queryKey: ["stats", "habits"], queryFn: api.habitStats });
}

export function useWeekReview() {
  return useQuery({ queryKey: ["stats", "review"], queryFn: api.weekReview });
}
